import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { NoDataItem } from '~/modules/common/components/NoDataItem';
import { MoveTimeDialog } from '~/modules/projects/components/ProjectListPage/MoveTimeDialog';
import { retryLoading } from '~/util';
import { useDialogState } from '~/modules/common/hooks';
import TaskBurndownSectionSkeleton from './TaskBurndownSectionSkeleton';
import TaskBurndownList from './TaskBurndownList/TaskBurndownList';
import { useTaskBurndownDataQuery } from './useTaskBurndownDataQuery';

export const TaskBurndownChart = React.lazy(() =>
  retryLoading(() =>
    import('~/modules/tasks/components/TaskDrawer/Charts/TaskBurndownChart')
  )
);

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    overflowY: 'visible',
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3)
    }
  },
  loading: {
    paddingTop: theme.spacing(3)
  },
  header: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1)
    },
    ...theme.typography.subtitle1,
    color: theme.palette.text.secondary
  },
  headerWithMoveTime: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1)
    },
    ...theme.typography.subtitle1,
    color: theme.palette.text.secondary,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  moveTimeButton: {
    fontSize: theme.typography.caption.fontSize,
    padding: theme.spacing(0.5, 1)
  },
  noData: {
    padding: theme.spacing(2)
  },
  noDataWithMoveTime: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

export const TaskBurndownSection = ({
  task,
  isDesktop,
  showMoveTimeButton
}) => {
  const classes = useStyles();
  const {
    open: moveTimeDialogOpen,
    closeDialog: closeMoveTimeDialog,
    openDialog: openMoveTimeDialog
  } = useDialogState(false, {
    disabledBackDropClick: true,
    disableEscapeKeyDown: true
  });

  const { taskBurndownData, loading } = useTaskBurndownDataQuery({ task });

  const moveTimeButton = useMemo(
    () =>
      showMoveTimeButton ? (
        <Button
          variant="outlined"
          size="small"
          className={classes.moveTimeButton}
          color="primary"
          onClick={openMoveTimeDialog}
          data-qe-id="MoveTimeDialogButton"
        >
          <FormattedMessage id="selectionBar.moveTime" />
        </Button>
      ) : null,
    [showMoveTimeButton, classes.moveTimeButton, openMoveTimeDialog]
  );

  return (
    <div className={classes.root}>
      {!loading && taskBurndownData.length === 0 ? (
        <>
          <div
            className={
              showMoveTimeButton ? classes.noDataWithMoveTime : classes.noData
            }
          >
            <NoDataItem>
              <FormattedMessage id="taskDrawer.No timesheet data available" />
            </NoDataItem>
            {moveTimeButton}
          </div>
        </>
      ) : (
        !loading && (
          <>
            {isDesktop && (
              <TaskBurndownChart
                task={task}
                timesheetEntries={taskBurndownData}
              />
            )}
            <div
              className={
                showMoveTimeButton ? classes.headerWithMoveTime : classes.header
              }
            >
              <FormattedMessage id="taskDrawer.taskHours" />
              {moveTimeButton}
            </div>
            <TaskBurndownList task={task} records={taskBurndownData} />
          </>
        )
      )}
      {loading && <TaskBurndownSectionSkeleton />}
      {showMoveTimeButton && moveTimeDialogOpen && (
        <MoveTimeDialog
          open={moveTimeDialogOpen}
          onClose={closeMoveTimeDialog}
          sourceProject={task.project}
          sourceTask={task.isTimeEntryAllowed ? task : undefined}
        />
      )}
    </div>
  );
};

TaskBurndownSection.propTypes = {
  task: PropTypes.object.isRequired,
  isDesktop: PropTypes.bool,
  showMoveTimeButton: PropTypes.bool
};

export default TaskBurndownSection;
